import CrossIcon from '@ingka/ssr-icon/paths/cross';
import { ComponentIds, ProppingSystem } from '@inter-ikea-core/core';
import { VpcService } from '@inter-ikea-kompis/services';
import { CoreApi, KompisSettings, saveFileToTemporaryStorage } from '@inter-ikea-storageone/range';
import { IGLTFFiles } from '@inter-ikea-betrakta/viewer';
import { useCore } from 'contexts/core';
import { useServiceSettings } from 'contexts/serviceSettings';
import { getShoppingProducts } from 'ducks/products';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import LoadingSpinner from 'ui/LoadingSpinner/LoadingSpinner';
import { blobToBase64 } from 'utils/imageUtils';
import { getRange } from 'utils/queryUtils';

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  background-color: #ffffffcf;
`;

const Content = styled.div`
  position: relative;
  margin: auto;
  background-color: white;
  padding: 1rem;
  border: 1px solid darkgrey;
  border-radius: 4px;
  min-width: 335px;
  min-height: 335px;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CloseButtonWrapper = styled(Box)`
  position: absolute;
  right: -16px;
  top: -16px;
  width: 32px;
`;

const ConfigurationText = styled.div`
  font-weight: 600;
  font-size: xx-large;
`;

interface IExportScene {
  vpcCode: string;
  configurationImage: string;
}

const ExportScene = ({ vpcCode, configurationImage }: IExportScene) => {
  const coreApi = useCore();
  const [visible, setVisible] = useState<boolean>(false);
  const [error, setError] = useState<{ error: string; message: string }>();
  const [configurationId, setConfigurationId] = useState<string | undefined>('');
  const { kompisSettings } = useServiceSettings();
  const shoppingProducts = useSelector(getShoppingProducts());

  const handleHotKeyPress = () => {
    coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();
    setVisible(true);

    getSceneAsFile(coreApi).then(file => {
      coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable();
      if (file) {
        const fileName = Object.keys(file)[0];
        const fileBlob = file[fileName] as Blob;
        if (fileBlob) {
          blobToBase64(fileBlob).then(fileAsBase64String => {
            saveFileToTemporaryStorage(fileAsBase64String, getRange()).then(response => {
              if (response.error) {
                setError({ error: response.error, message: response.message });
                setConfigurationId('');
              } else {
                getDesignCodeForUrl(
                  kompisSettings,
                  response.url,
                  shoppingProducts,
                  vpcCode,
                  configurationImage
                )
                  .then(result => {
                    setConfigurationId(result?.configurationId);
                    setError({ error: '', message: '' });
                  })
                  .catch(() => {
                    setError({
                      error: 'Something went wrong',
                      message: 'when retrieving configuration code'
                    });
                    setConfigurationId('');
                  });
              }
            });
          });
        }
      }
    });
  };

  useHotkeys('ctrl+q', handleHotKeyPress);

  if (!visible) {
    return <></>;
  }

  const onClose = () => {
    setError({ error: '', message: '' });
    setVisible(false);
    setConfigurationId('');
  };

  return (
    <Wrapper>
      <Content>
        <CloseButtonWrapper>
          <Button
            iconOnly
            ssrIcon={CrossIcon}
            size={'xsmall'}
            type={ButtonTypeEnum.primary}
            onClick={onClose}
          />
        </CloseButtonWrapper>
        {!configurationId && !error?.error && <LoadingSpinner />}
        {error?.error && (
          <>
            <div>
              <b>{error.error}</b>
            </div>
            <div>{error.message}</div>
          </>
        )}
        {configurationId && <ConfigurationText>{configurationId}</ConfigurationText>}
      </Content>
    </Wrapper>
  );
};

export default ExportScene;

const getSceneAsFile = (coreApi: CoreApi): Promise<IGLTFFiles | undefined> => {
  return new Promise<IGLTFFiles | undefined>(resolve => {
    const allEntitiesNoRoom = coreApi
      .getServices()
      .entityManager.entities.filter(
        e =>
          !e.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID) &&
          e.hasComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
      );

    const newObjects = new Set<number>();

    allEntitiesNoRoom.forEach(e => {
      const viewerObjectId = coreApi.entities.getObjectId(e);

      if (viewerObjectId) {
        // For some objects we need to get all the children. For e.g. cloth rails and l-dividers
        coreApi.ipexViewer
          .get_children(viewerObjectId, false, true)
          .forEach(c => newObjects.add(c));
      }
    });

    if (newObjects.size > 0) {
      resolve(coreApi.ipexViewer.serialize_glb(Array(...newObjects)));
    } else {
      resolve(undefined);
    }
  });
};

const getDesignCodeForUrl = async (
  kompisSettings: KompisSettings,
  savedGlbUrl: string,
  shoppingProducts: any,
  vpcCode: string,
  configurationImage: string
) => {
  const content = {
    configuration: {
      url: savedGlbUrl,
      'original-design-code': vpcCode,
      'application-id': kompisSettings.applicationName,
      'created-time': new Date().toISOString()
    }
  };

  const shoppingItems = shoppingProducts.map((p: any) => p.shoppingItem);

  const kompisSettingsCopy = { ...kompisSettings };
  kompisSettingsCopy.dexfApplicationId = 'ird-pm';
  kompisSettingsCopy.dexfConfigurationVersion = '0.1';

  const service = new VpcService(kompisSettingsCopy);
  return service.saveConfiguration(content, shoppingItems, configurationImage);
};
